/**
 * PasswordStrengthIndicator — Core 4-bar strength meter + rule checklist.
 *
 * Checks: length >= 8, has uppercase, has number, has special char.
 * Each check fills one bar (score 0-4).
 */

interface PasswordStrengthIndicatorProps {
  password: string;
  /** Optionally render the 4-rule checklist beneath the strength meter. */
  showRules?: boolean;
}

interface PasswordRule {
  label: string;
  ok: boolean;
}

const STRENGTH_LABELS = ['Weak', 'Fair', 'Good', 'Strong'] as const;
const STRENGTH_COLORS = [
  '#C8553D',
  '#D9A957',
  'var(--color-teal-dark)',
  'var(--color-teal-dark)',
] as const;

function computeRules(password: string): PasswordRule[] {
  return [
    { label: '8 or more characters', ok: password.length >= 8 },
    { label: 'One number', ok: /[0-9]/.test(password) },
    { label: 'One uppercase letter', ok: /[A-Z]/.test(password) },
    { label: 'One symbol', ok: /[^A-Za-z0-9]/.test(password) },
  ];
}

function computeScore(rules: PasswordRule[]): number {
  return rules.filter((r) => r.ok).length;
}

const RuleCheck = ({ ok }: { ok: boolean }) => (
  <span
    className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border"
    style={{
      background: ok ? 'var(--color-teal-light)' : 'var(--color-paper-2)',
      borderColor: ok
        ? 'var(--color-teal-dark)'
        : 'var(--color-warm-border-strong)',
    }}
  >
    {ok ? (
      <svg width="9" height="9" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path
          d="M2 6.5 5 9.5l5-7"
          stroke="var(--color-teal-dark)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ) : null}
  </span>
);

export function PasswordStrengthIndicator({
  password,
  showRules = false,
}: PasswordStrengthIndicatorProps) {
  if (!password) return null;

  const rules = computeRules(password);
  const score = computeScore(rules);
  const accentColor =
    score === 0
      ? 'var(--color-warm-border-strong)'
      : STRENGTH_COLORS[score - 1];
  const label = score === 0 ? '' : STRENGTH_LABELS[score - 1];

  return (
    <div className="mt-2 flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <div className="flex gap-1">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-1 flex-1 rounded-full transition-[background] duration-200"
              style={{
                background:
                  i < score ? accentColor : 'var(--color-warm-border)',
              }}
            />
          ))}
        </div>
        <div className="flex justify-between text-[12px] text-ink-3">
          <span>Password strength</span>
          {label && (
            <span style={{ color: accentColor, fontWeight: 500 }}>{label}</span>
          )}
        </div>
      </div>
      {showRules && (
        <ul className="m-0 grid list-none grid-cols-2 gap-x-4 gap-y-2 p-0">
          {rules.map((r) => (
            <li
              key={r.label}
              className="flex items-center gap-2 text-[12.5px]"
              style={{
                color: r.ok ? 'var(--color-ink-2)' : 'var(--color-ink-3)',
              }}
            >
              <RuleCheck ok={r.ok} />
              <span
                style={{
                  textDecoration: r.ok ? 'line-through' : 'none',
                  textDecorationColor: 'var(--color-ink-3)',
                }}
              >
                {r.label}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
